// ManagementDashboard - Dashboard du Management Hub avec sections déroulantes
import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  Users,
  Settings,
  Plug,
  TrendingUp,
  Loader2,
  ChevronDown,
  ChevronUp,
  PieChart,
  BarChart3,
  Activity,
  Shield,
  Key,
  Database,
  Server,
  CheckCircle,
  AlertTriangle,
  Clock,
  UserCheck,
  UserPlus,
  Crown,
  Zap
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Composant Section Déroulante
function CollapsibleSection({ title, icon: Icon, color, isOpen, onToggle, badge, children }) {
  const colorClasses = {
    emerald: "from-emerald-500 to-teal-500 text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    blue: "from-blue-500 to-cyan-500 text-blue-400 bg-blue-500/10 border-blue-500/20",
    purple: "from-purple-500 to-pink-500 text-purple-400 bg-purple-500/10 border-purple-500/20",
    amber: "from-amber-500 to-orange-500 text-amber-400 bg-amber-500/10 border-amber-500/20",
    zinc: "from-zinc-500 to-slate-500 text-zinc-400 bg-zinc-500/10 border-zinc-500/20"
  };

  const classes = colorClasses[color] || colorClasses.emerald;

  return (
    <div className={`rounded-xl border ${classes.split(' ').slice(3).join(' ')} overflow-hidden`}>
      <button
        onClick={onToggle}
        className={`w-full p-4 flex items-center justify-between ${classes.split(' ').slice(2, 3).join(' ')} hover:opacity-90 transition-opacity`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${classes.split(' ').slice(0, 2).join(' ')} flex items-center justify-center`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="text-white font-medium text-lg">{title}</span>
          {badge !== undefined && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${classes.split(' ').slice(2, 3).join(' ')} ${classes.split(' ').slice(1, 2).join(' ')}`}>
              {badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-zinc-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-zinc-400" />
        )}
      </button>
      
      {isOpen && (
        <div className="p-4 bg-zinc-900/30 border-t border-white/5">
          {children}
        </div>
      )}
    </div>
  );
}

// Mini Stat Card
function MiniStat({ icon: Icon, label, value, color = "zinc" }) {
  const colorClasses = {
    emerald: "bg-emerald-500/20 text-emerald-400",
    blue: "bg-blue-500/20 text-blue-400",
    purple: "bg-purple-500/20 text-purple-400",
    red: "bg-red-500/20 text-red-400",
    amber: "bg-amber-500/20 text-amber-400",
    zinc: "bg-zinc-500/20 text-zinc-400"
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-xs text-zinc-500">{label}</p>
        <p className="text-lg font-bold text-white">{value}</p>
      </div>
    </div>
  );
}

// Integration Status Card
function IntegrationStatus({ name, status, lastSync }) {
  const isConnected = status === "connected";
  return (
    <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
      <div className="flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
        <span className="text-white text-sm">{name}</span>
      </div>
      <div className="text-xs text-zinc-500">
        {isConnected ? lastSync : 'Déconnecté'}
      </div>
    </div>
  );
}

export default function ManagementDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // État des sections ouvertes
  const [openSections, setOpenSections] = useState({
    overview: true,
    users: false,
    integrations: false,
    system: false
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch various stats - ONLY from traceable sources
      const [callsRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/api/calls?limit=1000`, { headers: getAuthHeaders() }).catch(() => ({ data: { calls: [] } })),
        axios.get(`${API}/api/tickets/stats`, { headers: getAuthHeaders() }).catch(() => ({ data: {} }))
      ]);
      
      const calls = callsRes.data.calls || [];
      const ticketStats = ticketsRes.data || {};
      
      // Calculate resolution rate from actual ticket data
      const totalResolvedAndOpen = (ticketStats.by_status?.open || 0) + (ticketStats.by_status?.resolved || 0);
      const resolutionRate = totalResolvedAndOpen > 0 
        ? Math.round((ticketStats.by_status?.resolved || 0) / totalResolvedAndOpen * 100) 
        : 0;
      
      setStats({
        // Overview - ✅ FIABLE (basé sur collections MongoDB)
        total_appels: calls.length,
        total_tickets: (ticketStats.by_status?.open || 0) + (ticketStats.by_status?.in_progress || 0) + (ticketStats.by_status?.resolved || 0),
        taux_resolution: resolutionRate,
        activite_semaine: ticketStats.recent_7_days || 0
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
      setStats({
        total_appels: 0, total_tickets: 0, taux_resolution: 0, activite_semaine: 0
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
          <LayoutDashboard className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-white">Management Hub</h1>
          <p className="text-sm text-zinc-400">Administration et paramétrage global</p>
        </div>
      </div>

      {/* Bienvenue */}
      {user && (
        <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Crown className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-white font-medium">Bienvenue, {user.name || user.email}</p>
                <p className="text-sm text-zinc-400">Vous êtes connecté en tant qu&apos;administrateur</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Résumé global - Statistiques fiables uniquement */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Activity className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-zinc-500">Appels total</p>
              <p className="text-xl font-bold text-white">{stats.total_appels}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-zinc-500">Tickets total</p>
              <p className="text-xl font-bold text-white">{stats.total_tickets}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-zinc-500">Taux résolution</p>
              <p className="text-xl font-bold text-white">{stats.taux_resolution}%</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <Zap className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-zinc-500">Activité semaine</p>
              <p className="text-xl font-bold text-white">{stats.activite_semaine}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sections déroulantes */}
      <div className="space-y-3">
        
        {/* 📊 VUE D'ENSEMBLE - ✅ FIABLE */}
        <CollapsibleSection
          title="Vue d'ensemble"
          icon={PieChart}
          color="emerald"
          isOpen={openSections.overview}
          onToggle={() => toggleSection('overview')}
          badge="Données fiables"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat icon={Activity} label="Appels total" value={stats.total_appels} color="blue" />
            <MiniStat icon={BarChart3} label="Tickets total" value={stats.total_tickets} color="purple" />
            <MiniStat icon={TrendingUp} label="Taux résolution" value={`${stats.taux_resolution}%`} color="emerald" />
            <MiniStat icon={Zap} label="Activité semaine" value={stats.activite_semaine} color="amber" />
          </div>
          <p className="text-xs text-zinc-500 mt-3 italic">
            ✅ Statistiques calculées à partir des collections MongoDB (calls, tickets)
          </p>
        </CollapsibleSection>

        {/* 👥 UTILISATEURS - Module non actif */}
        <CollapsibleSection
          title="Utilisateurs & Rôles"
          icon={Users}
          color="purple"
          isOpen={openSections.users}
          onToggle={() => toggleSection('users')}
          badge="Non disponible"
        >
          <div className="text-center py-6">
            <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">Module non implémenté</p>
            <p className="text-zinc-500 text-xs mt-1">Aucune donnée disponible</p>
          </div>
        </CollapsibleSection>

        {/* 🔌 INTÉGRATIONS - Statut statique */}
        <CollapsibleSection
          title="Intégrations API"
          icon={Plug}
          color="amber"
          isOpen={openSections.integrations}
          onToggle={() => toggleSection('integrations')}
          badge="Actives"
        >
          <div className="space-y-2">
            <IntegrationStatus name="Twilio (Voice & SMS)" status="connected" lastSync="Actif" />
            <IntegrationStatus name="OpenAI (GPT-4o)" status="connected" lastSync="Actif" />
            <IntegrationStatus name="Google Sheets" status="connected" lastSync="Actif" />
            <IntegrationStatus name="MongoDB" status="connected" lastSync="Actif" />
            <IntegrationStatus name="WhatsApp Business" status="disconnected" lastSync="" />
            <IntegrationStatus name="ElevenLabs (TTS)" status="disconnected" lastSync="Quota épuisé" />
          </div>
          <p className="text-xs text-zinc-500 mt-3 italic">
            ⚠️ Statuts d&apos;intégration manuels - À automatiser dans une future version
          </p>
        </CollapsibleSection>

        {/* ⚙️ SYSTÈME - Non traçable */}
        <CollapsibleSection
          title="Système"
          icon={Server}
          color="zinc"
          isOpen={openSections.system}
          onToggle={() => toggleSection('system')}
          badge="Info"
        >
          <div className="text-center py-6">
            <Server className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">Métriques système non disponibles</p>
            <p className="text-zinc-500 text-xs mt-1">Les indicateurs de santé système seront ajoutés après déploiement sur infrastructure dédiée</p>
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
}
